import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Update, GetAllEmployees, GetById } from "adminApi/HousekeepingApi/Housekeeping.js";
import { validateForm } from "utils/validation";
import { HousekeepingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import fields from "../json/HousekeepingFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import EditHousekeepingForm from "./components/EditHousekeepingForm";

/**
 * EditHousekeeping — smart container
 * Fetches existing record by ID, pre-fills the form, and handles update submit.
 */
const EditHousekeeping = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { formdata, searchdata } = fields;

    // Include room_ids in allFields so useFetchFormDataById pre-fills it
    const allFields = useMemo(
        () => [...formdata, ...searchdata, { name: "limit" }, { name: "room_ids" }],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const initialValues = useMemo(
        () => allFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
        [allFields]
    );

    const { values, errors, handleChange, setErrors, setValues } = useFormChange(
        initialValues,
        HousekeepingRules
    );

    // Pre-fill form with existing record
    const { loading: loadingFetch } = useFetchFormDataById(id, allFields, GetById, setValues);

    // Managers and Cleaners — pre-filtered at DB level
    const { data: employeeData } = useFetchAllData(GetAllEmployees);
    const managers = employeeData?.managers ?? [];
    const cleaners = employeeData?.cleaners ?? [];

    // Submit handler
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            const formData = new FormData();

            const roomIdsArray = Array.isArray(formValues.room_ids)
                ? formValues.room_ids
                : typeof formValues.room_ids === "string"
                    ? formValues.room_ids.split(",").map((v) => v.trim()).filter(Boolean)
                    : [];

            roomIdsArray.forEach((rid) => formData.append("room_ids[]", rid));

            const { room_ids: _skip, ...rest } = formValues;
            Object.entries(rest).forEach(([key, val]) => formData.append(key, val ?? ""));

            return await Update({ id, data: formData });
        },
        (vals) => {
            const err = validateForm(vals, HousekeepingRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Housekeeping task updated successfully."
    );

    const submitPayload = {
        assigned_by: values.assigned_by,
        assigned_to: values.assigned_to,
        start_time: values.start_time,
        end_time: values.end_time,
        room_ids: values.room_ids,
        inventory_issued: values.inventory_issued,
    };

    return (
        <>
            {(loadingSubmit || loadingFetch) && <Spinner isSpinner />}
            <EditHousekeepingForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, submitPayload)}
                loading={loadingSubmit}
                managers={managers}
                cleaners={cleaners}
                setValues={setValues}
            />
        </>
    );
};

export default EditHousekeeping;
